import React from "react";
import { Package, Save, RotateCcw } from "lucide-react";
import { Button, Input, Select } from "./ui-kit";
import { formatItalianDate } from "../lib/dateUtils";
import { computePack, MASS_UNITS, netQuantityLine, PACK_MODES, SINGLE_UNITS } from "../lib/netQuantity";

export const PACK_KEYS = ["packMode", "packPieces", "packPieceValue", "packPieceUnit", "packTotalValue", "packTotalUnit"];

const QUICK = [["500", "g"], ["1", "kg"], ["2", "kg"], ["2,5", "kg"], ["3", "kg"], ["5", "kg"]];

const chipCls = (active) => `min-h-[44px] rounded-lg border px-4 text-sm font-semibold text-navy transition-all ${active ? "border-gold bg-gold/10 ring-2 ring-gold/20" : "border-hairline bg-white hover:border-gold/60"}`;

function Labeled({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function AmountInput({ value, unit, units, onValue, onUnit, placeholder, testid, unitLabels = {} }) {
  return (
    <div className="flex gap-2">
      <Input inputMode="decimal" value={value} onChange={(e) => onValue(e.target.value)} placeholder={placeholder} data-testid={testid} />
      <div className="w-24 shrink-0">
        <Select value={unit} onChange={(e) => onUnit(e.target.value)} aria-label="Unità di misura" data-testid={`${testid}-unit`}>
          {units.map((u) => <option key={u} value={u}>{unitLabels[u] || u}</option>)}
        </Select>
      </div>
    </div>
  );
}

/**
 * Quantità netta e composizione della confezione (prodotto singolo, multipla a
 * peso fisso, multipla a peso variabile). I valori sono campi di labelData;
 * onChange(patch) li riceve e il genitore ricalcola netQuantity con withPack().
 */
export default function NetQuantitySection({ data, onChange, consumer = false, defaults, canSaveDefault, onSaveDefault, saving }) {
  const mode = data.packMode || "single";
  const result = computePack(data);
  const touched = mode === "single" ? Boolean(String(data.packTotalValue || "").trim())
    : [data.packPieces, mode === "fixed" ? data.packPieceValue : data.packTotalValue].some((v) => String(v || "").trim());
  const legacy = mode === "single" && !String(data.packTotalValue || "").trim() && (data.netQuantity || "").trim();
  const net = legacy ? data.netQuantity : result.net;
  const defaultsResult = defaults ? computePack(defaults) : null;
  const defaultsText = defaults ? netQuantityLine(defaultsResult.net || defaults.netQuantity, defaultsResult.composition) : "";
  const currentText = net ? netQuantityLine(net, result.composition) : "";
  const isDefault = Boolean(defaults) && defaultsText === currentText;

  const chooseMode = (key) => {
    const patch = { packMode: key };
    if (key === "fixed") patch.packTotalUnit = "";
    if (key === "variable" && !MASS_UNITS.includes(data.packTotalUnit)) patch.packTotalUnit = "kg";
    if (key === "single" && !SINGLE_UNITS.includes(data.packTotalUnit)) patch.packTotalUnit = "g";
    onChange(patch);
  };

  return (
    <section className="rounded-xl border-2 border-gold/40 bg-gold/5 p-4" data-testid="net-quantity-section">
      <h4 className="flex items-center gap-2 font-heading font-semibold text-navy"><Package className="h-4 w-4 text-gold" /> Quantità netta e composizione della confezione</h4>

      <div className="mt-3 grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Tipologia di confezionamento">
        {PACK_MODES.map(({ key, label, hint }) => (
          <button key={key} type="button" role="radio" aria-checked={mode === key} onClick={() => chooseMode(key)}
            className={`rounded-lg border p-3 text-left transition-all ${mode === key ? "border-gold bg-white ring-2 ring-gold/30" : "border-hairline bg-white/70 hover:border-gold/60"}`} data-testid={`pack-mode-${key}`}>
            <span className="block text-sm font-semibold text-navy">{label}</span>
            <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">{hint}</span>
          </button>
        ))}
      </div>

      {mode === "single" && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {QUICK.map(([value, unit]) => (
              <button key={`${value}${unit}`} type="button" className={chipCls(data.packTotalValue === value && data.packTotalUnit === unit)}
                onClick={() => onChange({ packTotalValue: value, packTotalUnit: unit })} data-testid={`pack-quick-${value}${unit}`}>{value} {unit}</button>
            ))}
          </div>
          <Labeled label="Quantità netta" className="max-w-xs">
            <AmountInput value={data.packTotalValue || ""} unit={SINGLE_UNITS.includes(data.packTotalUnit) ? data.packTotalUnit : "g"} units={SINGLE_UNITS}
              onValue={(v) => onChange({ packTotalValue: v })} onUnit={(u) => onChange({ packTotalUnit: u })} placeholder="es. 150" testid="pack-single-value" />
          </Labeled>
          {legacy && <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">Quantità salvata in precedenza: <strong>«{data.netQuantity}»</strong>. Resta così finché non inserisci un nuovo valore.</p>}
          {consumer && !net && <p className="text-xs text-slate-500">Se lasci vuoto si usa la quantità predefinita della scheda etichetta.</p>}
        </div>
      )}

      {mode !== "single" && (
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Labeled label="Numero di pezzi">
            <Input inputMode="numeric" value={data.packPieces || ""} onChange={(e) => onChange({ packPieces: e.target.value.replace(/\s/g, "") })} placeholder="es. 20" data-testid="pack-pieces" />
          </Labeled>
          {mode === "fixed" ? (
            <Labeled label="Peso netto del singolo pezzo">
              <AmountInput value={data.packPieceValue || ""} unit={MASS_UNITS.includes(data.packPieceUnit) ? data.packPieceUnit : "g"} units={MASS_UNITS}
                onValue={(v) => onChange({ packPieceValue: v })} onUnit={(u) => onChange({ packPieceUnit: u })} placeholder="es. 100" testid="pack-piece-value" />
            </Labeled>
          ) : (
            <Labeled label="Quantità netta complessiva rilevata">
              <AmountInput value={data.packTotalValue || ""} unit={MASS_UNITS.includes(data.packTotalUnit) ? data.packTotalUnit : "kg"} units={MASS_UNITS}
                onValue={(v) => onChange({ packTotalValue: v })} onUnit={(u) => onChange({ packTotalUnit: u })} placeholder="es. 2,35" testid="pack-total-value" />
            </Labeled>
          )}
          <div>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Quantità netta totale</span>
            <div className="flex min-h-[44px] items-center gap-2 rounded-lg border border-hairline bg-white px-3">
              <span className="flex-1 font-heading text-lg font-bold text-navy" aria-live="polite" data-testid="pack-total">{result.net || "—"}</span>
              {mode === "fixed" && (
                <select className="rounded border border-hairline bg-white px-1 py-1 text-xs text-navy" value={data.packTotalUnit || ""} onChange={(e) => onChange({ packTotalUnit: e.target.value })} aria-label="Unità del totale" data-testid="pack-total-unit">
                  <option value="">auto</option><option value="g">g</option><option value="kg">kg</option>
                </select>
              )}
            </div>
            <span className="mt-1 block text-[11px] text-slate-400">{mode === "fixed" ? "Calcolata: pezzi × peso unitario." : "Nessun peso unitario viene attribuito ai pezzi."}</span>
          </div>
        </div>
      )}

      {touched && result.error && <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700" data-testid="pack-error">{result.error}</p>}
      {currentText && !result.error && (
        <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900" data-testid="pack-line">In etichetta: <strong>{currentText}</strong></p>
      )}

      {(defaults || canSaveDefault) && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gold/20 pt-3 text-xs text-slate-600">
          <span data-testid="pack-defaults-info">{defaults
            ? <>Abituale per questo prodotto: <strong className="text-navy">{defaultsText}</strong>{defaults.updated_by_name ? ` · salvata da ${defaults.updated_by_name} il ${formatItalianDate(defaults.updated_at)}` : ""}</>
            : "Nessuna configurazione abituale salvata per questo prodotto."}</span>
          <span className="flex flex-wrap gap-2">
            {defaults && !isDefault && <Button variant="ghost" onClick={() => onChange(Object.fromEntries(PACK_KEYS.map((k) => [k, defaults[k] ?? ""])), defaults.netQuantity)} data-testid="pack-use-default"><RotateCcw className="h-4 w-4" /> Usa abituale</Button>}
            {canSaveDefault && !isDefault && <Button variant="outline" loading={saving} disabled={!net || Boolean(result.error)} onClick={onSaveDefault} data-testid="pack-save-default"><Save className="h-4 w-4" /> Salva come abituale</Button>}
          </span>
        </div>
      )}
    </section>
  );
}
